/* eslint-disable object-curly-newline */

import Vue from 'vue';
import axios from 'axios';

// HELPER METHODS
const descendants = ((collection) => {
  const list = [];
  return {
    empty: function() { // eslint-disable-line 
      list.length = 0;
    },
    gather: function(collection) { // eslint-disable-line 
      collection.children.forEach((child) => {
        list.push(child.id);
        if (child.children.length) descendants.gather(child);
      });
      return list;
    },
    value: function() { // eslint-disable-line 
      return list;
    },
  };
})();

// REGULAR VUEX

const state = {
  collectionNodes: null,
  archivedCollectionNodes: null,
  selectedCollection: Number(localStorage.getItem('selectedCollection')) || null,
};

const getters = {
  getCardsOfSelectedCollection(state, getters, rootState) {
    const cards = rootState.cards.cards
      ? rootState.cards.cards[state.selectedCollection] || []
      : [];
    return cards;
  },
  getNotesOfSelectedCollection(state, getters, rootState) {
    const notes = rootState.notes.notes
      ? rootState.notes.notes[state.selectedCollection] || []
      : [];
    return notes;
  },
  userId: (state, getters, rootState, rootGetters) => rootGetters['users/userId'],
  allCollectionIds: state => Object.keys(Object.assign({}, state.collectionNodes, state.archivedCollectionNodes)).map(id => Number(id)),
};

const actions = {
  setCollections({ commit }, collections) {
    const collectionNodes = {};
    const archivedCollectionNodes = {};

    // First construct a node for each collection
    collections.forEach((collection) => {
      const nodeObj = {
        name: collection.name,
        id: collection.collectionId,
        archived: !!collection.archived,
        shared: !!collection.isShared,
        parent: null,
        children: [],
      };
      if (!collection.archived) collectionNodes[collection.collectionId] = nodeObj;
      else archivedCollectionNodes[collection.collectionId] = nodeObj;
    });

    // Then get rid of the 'invisible root collection node', for that one does not have a parent
    collections.splice(collections.findIndex(collection => collection.collectionId === 0), 1);

    // Then fill in the parents and children (can only be done now that we already have all collection nodes)
    collections.forEach((collection) => {
      if (!collection.archived) {
        collectionNodes[collection.collectionId].parent = collectionNodes[collection.parentCollectionId];
        collectionNodes[collection.parentCollectionId].children.push(collectionNodes[collection.collectionId]);
      }
      else {
        if (collection.parentCollectionId) { // eslint-disable-line 
          // Collections that do not have root collection as parent
          archivedCollectionNodes[collection.collectionId].parent = archivedCollectionNodes[collection.parentCollectionId];
          archivedCollectionNodes[collection.parentCollectionId].children.push(archivedCollectionNodes[collection.collectionId]);
        }
        else {
          // Collections that have the root collection as parent (no need to fill in the parent, the getter for archivedCollectionNodes takes care of that for archived root children)
          archivedCollectionNodes[collection.collectionId].parent = collectionNodes[collection.parentCollectionId];
        }
      }
    });

    commit('SetCollectionTree', collectionNodes);
    commit('SetArchivedCollectionTree', archivedCollectionNodes);
  },
  getAndProcessNewCollectionId({ dispatch, commit, getters }, newCollectionData) {
    // Some validation
    if (!newCollectionData.newCollection.name
      || (!newCollectionData.newMembership.parentCollectionId && newCollectionData.newMembership.parentCollectionId !== 0)
      || !newCollectionData.newMembership.relatedUserId) {
      dispatch('notifications/setError', { error: 'Error: Failed to add collection' }, { root: true });
      return;
    }

    axios.post(`collections/${getters.userId}`, newCollectionData)
      .then((res) => {
        const { newId, tempId } = res.data;
        commit('ReplaceNewCollectionId', { newId, tempId });
        commit('SelectCollection', newId);
      })
      .catch((err) => {
        if (err.message === 'Network Error') {
          const customMessage = 'Could not save changes due to a problem with the network. Please check your internet connection.';
          dispatch('notifications/setError', { error: err, customMessage }, { root: true });
        }
        else dispatch('notifications/setError', { error: err }, { root: true });
      });
  },
  addCollection({ state, dispatch, commit }, { newCollection, newMembership }) {
    return new Promise((resolve, reject) => {
      // Copy incoming data, to send to getAndProcessNewCollectionId lateron
      const collectionData = {};
      collectionData.newCollection = Object.assign({}, newCollection);
      delete collectionData.newCollection.notes;
      collectionData.newMembership = Object.assign({}, newMembership);

      // Some validation
      if (!newCollection.name
          || (!newMembership.parentCollectionId && newMembership.parentCollectionId !== 0)
          || !newMembership.relatedUserId) {
        dispatch('notifications/setError', { error: 'Error: Failed to add collection' }, { root: true });
        reject(new Error('Failed to add collection (validation failed)'));
        return;
      }

      // Validation passed, construct node
      const collectionNode = newCollection;
      // Give a negative temporary id to the collection. Without pending new collections, minimum id is 0 (root collection) and new id thus -1. With pending collections, new id is -2, -3, etc
      collectionNode.id = Math.min(...Object.keys(state.collectionNodes)) - 1;
      collectionNode.parent = state.collectionNodes[newMembership.parentCollectionId];
      collectionNode.children = [];
      commit('AddNewCollection', collectionNode);
      commit('AddNewCollectionToParent', collectionNode);

      const requestNewCollectionIdPayload = Object.assign({}, collectionData, { tempId: collectionNode.id });
      dispatch('getAndProcessNewCollectionId', requestNewCollectionIdPayload);

      resolve(collectionNode.id);
    });
  },
  moveCollection({ state, dispatch, commit }, { newParentId, collectionId }) {
    if ((!newParentId && newParentId !== 0) || !collectionId) {
      dispatch('notifications/setError', { error: 'Error: failed to move collection (validation failed)' }, { root: true });
      return;
    }
    if (newParentId === collectionId) return; // A collection cannot be its own parent

    const oldParentId = state.collectionNodes[collectionId].parent.id;
    const oldChildIndex = state.collectionNodes[oldParentId].children.findIndex(child => child.id === collectionId);
    commit('MoveCollection', { collectionId, oldParentId, oldChildIndex, newParentId });

    dispatch('sync/enqueueMembership', { action: 'move', collectionId, newParentId }, { root: true });
  },
  archiveCollection({ state, dispatch, commit }, collectionId) {
    if (!collectionId) {
      dispatch('notifications/setError', { error: 'Error: failed to archive collection (validation failed)' }, { root: true });
      return;
    }

    descendants.empty();
    const descendantCollections = descendants.gather(state.collectionNodes[collectionId]);

    const oldParentId = state.collectionNodes[collectionId].parent.id;
    const oldChildIndex = state.collectionNodes[oldParentId].children.findIndex(child => child.id === collectionId);
    commit('ArchiveCollection', { collectionId, descendantCollections, oldParentId, oldChildIndex });

    dispatch('sync/enqueueMembership', { action: 'archive', collectionId, descendantCollections }, { root: true });
  },
  unarchiveCollection({ state, dispatch, commit }, collectionId) {
    if (!collectionId) {
      dispatch('notifications/setError', { error: 'Error: failed to unarchive collection (validation failed)' }, { root: true });
      return;
    }

    descendants.empty();
    const descendantCollections = descendants.gather(state.archivedCollectionNodes[collectionId]);

    // A collection that we want to unarchive either is a child of another archived collection, or it has parent id of 0 (without being a child of the 'root' collection).
    // If we cannot find the parent in archivedCollectionNodes, it has parent id of 0 and we set oldParent and oldChildIndex to null since we do not have to remove this
    // collection from a current parent; we can just add it to the children of the 'root' collection.
    const oldParentId = state.archivedCollectionNodes[collectionId].parent.id;
    const oldParent = state.archivedCollectionNodes[oldParentId] || null;
    const oldChildIndex = oldParent
      ? oldParent.children.findIndex(child => child.id === collectionId)
      : null;
    commit('UnarchiveCollection', { collectionId, descendantCollections, oldParentId, oldChildIndex });

    dispatch('sync/enqueueMembership', { action: 'unarchive', collectionId, descendantCollections }, { root: true });
  },
  updateCollection({ dispatch, commit }, { collectionId, name }) {
    if (!collectionId || !name) {
      dispatch('notifications/setError', { error: 'Error: failed to update collection (validation failed)' }, { root: true });
      return;
    }

    commit('UpdateCollection', { collectionId, name });

    dispatch('sync/enqueueCollection', { action: 'update', id: collectionId, name }, { root: true });
  },
  async deleteCollection({ state, dispatch, commit }, { collectionId, parentId }) {
    if (!collectionId || (!parentId && parentId !== 0)) {
      dispatch('notifications/setError', { error: 'Error: failed to delete collection (validation failed)' }, { root: true });
      return;
    }

    // Gather the descendant projects that need to be deleted
    descendants.empty();
    const descendantCollections = descendants.gather(state.collectionNodes[collectionId]);

    // Delete from the parent first (so that it's not rendered in the sidebar etc anymore)
    const indexAsChild = state.collectionNodes[parentId].children.findIndex(collection => collection.id === collectionId);
    commit('DeleteFromParent', { parentId, indexAsChild });

    /*
    Both for the collection itself and for its children, the following actions need to be carried out:
    1) Delete all cards in that collection
    2) Delete all notes in that collection
    3) Delete that collection from navigationHistory
    4) Remove the entry for selectedItems, if there is any

    Deleting the related cards and notes from navigationHistory is done in the actions that delete those cards and notes.
    */

    dispatch('cards/deleteAllCardsFromCollection', collectionId, { root: true });
    dispatch('notes/deleteAllNotesFromCollection', collectionId, { root: true });
    commit('appState/DeleteFromHistory', `/collection/${collectionId}`, { root: true });
    dispatch('appState/unselectItemsFrom', collectionId, { root: true });

    descendantCollections.forEach((descendantCollectionId) => {
      dispatch('cards/deleteAllCardsFromCollection', descendantCollectionId, { root: true });
      dispatch('notes/deleteAllNotesFromCollection', descendantCollectionId, { root: true });
      commit('appState/DeleteFromHistory', `/collection/${descendantCollectionId}`, { root: true });
      dispatch('appState/unselectItemsFrom', descendantCollectionId, { root: true });
    });

    dispatch('sync/enqueueCollection', { action: 'delete', id: collectionId, descendantCollections }, { root: true });

    const waitFor = (duration) => { // eslint-disable-line arrow-body-style
      return new Promise((resolve, reject) => { // eslint-disable-line arrow-body-style
        setTimeout(() => resolve(), duration);
      });
    };

    // The collection should only be deleted from Vuex once the DOM is re-rendered without the collection (since it's removed from the parent already)
    await waitFor(100);
    commit('DeleteCollection', { collectionId });
    descendantCollections.forEach(id => commit('DeleteCollection', { collectionId: id }));
  },
  selectCollection({ dispatch, commit }, { collectionId }) {
    // Some initial validation (is this necessary? Navigation has its own validation, and that's the only point where collections are selected right?)
    if (!collectionId) {
      dispatch('notifications/setError', { error: 'Error: failed to select collection (validation failed)' }, { root: true });
      return;
    }

    commit('SelectCollection', collectionId);
  },
};

const mutations = {
  SetCollectionTree(state, payload) {
    Vue.set(state, 'collectionNodes', payload);
  },
  SetArchivedCollectionTree(state, payload) {
    Vue.set(state, 'archivedCollectionNodes', payload);
  },
  AddNewCollection(state, collectionNode) {
    Vue.set(state.collectionNodes, collectionNode.id, collectionNode);
  },
  AddNewCollectionToParent(state, collectionNode) {
    state.collectionNodes[collectionNode.parent.id].children.push(collectionNode);
  },
  ReplaceNewCollectionId(state, { newId, tempId }) {
    Vue.set(state.collectionNodes, newId, state.collectionNodes[tempId]);
    state.collectionNodes[newId].id = newId;
    Vue.delete(state.collectionNodes, tempId);
  },
  MoveCollection(state, { collectionId, oldParentId, oldChildIndex, newParentId }) {
    state.collectionNodes[collectionId].parent = state.collectionNodes[newParentId];
    state.collectionNodes[oldParentId].children.splice(oldChildIndex, 1);
    state.collectionNodes[newParentId].children.push(state.collectionNodes[collectionId]);
  },
  ArchiveCollection(state, { collectionId, descendantCollections, oldParentId, oldChildIndex }) {
    // Delete from parent
    state.collectionNodes[oldParentId].children.splice(oldChildIndex, 1);

    // Set parent of archived 'root collection' to the root collection
    state.collectionNodes[collectionId].parent = state.collectionNodes['0'];

    // Move all collections (i.e. archived collection + descendants)
    descendantCollections.push(collectionId);
    descendantCollections.forEach((id) => {
      state.collectionNodes[id].archived = true;
      Vue.set(state.archivedCollectionNodes, id, state.collectionNodes[id]);
      Vue.delete(state.collectionNodes, id);
    });
  },
  UnarchiveCollection(state, { collectionId, descendantCollections, oldParentId, oldChildIndex }) {
    // Remove collection from original parent if applicable, set parent of unarchived collection to the root collection
    if (oldParentId && (oldChildIndex || oldChildIndex === 0)) state.archivedCollectionNodes[oldParentId].children.splice(oldChildIndex, 1);
    state.archivedCollectionNodes[collectionId].parent = state.collectionNodes['0'];

    // Unarchive all collections (i.e. unarchived collection + descendants)
    descendantCollections.push(collectionId);
    descendantCollections.forEach((id) => {
      const thisCollection = state.archivedCollectionNodes[id];

      thisCollection.archived = false;
      Vue.set(state.collectionNodes, id, state.archivedCollectionNodes[id]);
      Vue.delete(state.archivedCollectionNodes, id);

      // Adding unarchived collection to root collection
      if (thisCollection.parent.id === 0) state.collectionNodes['0'].children.push(thisCollection);
    });
  },
  UpdateCollection(state, { collectionId, name }) {
    if (state.collectionNodes[collectionId]) state.collectionNodes[collectionId].name = name;
    else if (state.archivedCollectionNodes[collectionId]) state.archivedCollectionNodes[collectionId].name = name;
  },
  DeleteFromParent(state, { parentId, indexAsChild }) {
    state.collectionNodes[parentId].children.splice(indexAsChild, 1);
  },
  DeleteCollection(state, { collectionId }) {
    Vue.delete(state.collectionNodes, collectionId);
  },
  SelectCollection(state, collectionId) {
    state.selectedCollection = collectionId;
    localStorage.setItem('selectedCollection', collectionId);
  },
  ResetState(state) {
    state.collectionNodes = {};
    state.archivedCollectionNodes = {};
    state.selectedCollection = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
